export interface MatchableItem {
    [key: string]: any;
}

/**
 * Filters an array of objects based on a partial, case-insensitive match of a specified property.
 *
 * @template T
 * @param {string} input - The search string to match against.
 * @param {T[]} list - The array of objects to search through.
 * @param {keyof T} searchProp - The property of the objects to search in.
 * @returns {T[]} A new array containing all objects where the specified property partially matches the input string.
 *
 * @example
 * const carBrands = [
 *     { brand: 'BMW', value: 'bmw' },
 *     { brand: 'Alfa Romeo', value: 'alfa-romeo' },
 *     { brand: 'Mercedes-Benz', value: 'mercedes' },
 *     { brand: 'Toyota', value: 'toyota' },
 * ];
 *
 * const result = partialMatch('rome', carBrands, 'brand');
 * console.log(result);
 * // Output: [{ brand: 'Alfa Romeo', value: 'alfa-romeo' }]
 *
 * @example
 * // Case-insensitive matching
 * const result2 = partialMatch('BM', carBrands, 'brand');
 * console.log(result2);
 * // Output: [{ brand: 'BMW', value: 'bmw' }]
 *
 * @example
 * // Matching against a different property
 * const result3 = partialMatch('alfa', carBrands, 'value');
 * console.log(result3);
 * // Output: [{ brand: 'Alfa Romeo', value: 'alfa-romeo' }]
 *
 * @description
 * The function performs a case-insensitive, partial match on the specified property.
 * It creates a new array and does not modify the input array.
 * If no matches are found, it returns an empty array.
 * If an empty string is provided as input, it returns the entire list.
 * The function can handle properties with non-string values by converting them to strings before comparison.
 * When multiple items match, all matching items are returned in the resulting array.
 *
 * @throws {TypeError} If searchProp is not a property of the objects in the list.
 */
export const partialMatch = <T extends MatchableItem>(
    input: string,
    list: T[],
    searchProp: keyof T
): T[] => {
    const lowercaseInput = input.toLowerCase();
    return list.filter((item) => String(item[searchProp]).toLowerCase().includes(lowercaseInput));
};

/**
 * Combines two arrays of objects into a single array, removing duplicates based on a specified key.
 * If duplicate keys are found, the function prioritizes the values from the second list.
 *
 * @template T
 * @param {T[]} list1 - The first array of objects to merge.
 * @param {T[]} list2 - The second array of objects to merge.
 * @param {keyof T} uniqueKey - The key to use for identifying unique objects.
 * @returns {T[]} A new array containing the merged and uniqued objects from both input lists.
 *
 * @example
 * const list1 = [
 *     { key: 'apple', value: '' },
 *     { key: 'banana', value: '' },
 *     { key: 'cherry', value: '' },
 * ];
 *
 * const list2 = [
 *     { key: 'apple', value: 'apple' },
 *     { key: 'date', value: 'date' },
 * ];
 *
 * const result = mergeUniqueLists(list1, list2, 'key');
 * console.log(result);
 * // Output:
 * // [
 * //     { key: 'apple', value: 'apple' },
 * //     { key: 'banana', value: '' },
 * //     { key: 'cherry', value: '' },
 * //     { key: 'date', value: 'date' }
 * // ]
 *
 * @example
 * // Using a different key
 * interface User {
 *     id: number;
 *     name: string;
 *     role: string;
 * }
 *
 * const users1: User[] = [
 *     { id: 1, name: 'Alice', role: 'Admin' },
 *     { id: 2, name: 'Bob', role: 'User' },
 * ];
 *
 * const users2: User[] = [
 *     { id: 2, name: 'Bob', role: 'Editor' },
 *     { id: 3, name: 'Charlie', role: 'User' },
 * ];
 *
 * const mergedUsers = mergeUniqueLists(users1, users2, 'id');
 * console.log(mergedUsers);
 * // Output:
 * // [
 * //     { id: 1, name: 'Alice', role: 'Admin' },
 * //     { id: 2, name: 'Bob', role: 'Editor' },
 * //     { id: 3, name: 'Charlie', role: 'User' }
 * // ]
 *
 * @description
 * The function creates a new array and does not modify the input arrays.
 * If an object in list2 has the same uniqueKey value as an object in list1,
 * the object from list2 will be in the final array.
 * The order of items in the resulting array is not guaranteed to match
 * the order of items in the input arrays.
 * If either list1 or list2 is empty, the function will return the non-empty list
 * (or an empty array if both are empty).
 * The function assumes that all objects in both lists have the property
 * specified by uniqueKey. If this is not the case, a runtime error may occur.
 *
 * @throws {TypeError} If uniqueKey is not a property of the objects in the lists.
 *
 * @see For more complex merging scenarios, consider using a custom comparison function.
 */
export function mergeTwoListsAsUnique<T>(list1: T[], list2: T[], uniqueKey: keyof T): T[] {
    const mergedMap = new Map<any, T>();

    // Process list1
    for (const item of list1) {
        mergedMap.set(item[uniqueKey], item);
    }

    // Process list2, overwriting any duplicate keys from list1
    for (const item of list2) {
        mergedMap.set(item[uniqueKey], item);
    }

    // Convert the Map back to an array
    return Array.from(mergedMap.values());
}

export const sortListByProperty = <T>(list: T[], property: keyof T, isAscending = true): T[] => {
    return [...list].sort((a, b) => {
        if (a[property] < b[property]) return isAscending ? -1 : 1;
        if (a[property] > b[property]) return isAscending ? 1 : -1;
        return 0;
    });
};
